import { CloseCircleOutlined, InboxOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Modal, Row, Tag, Upload, message } from "antd";
import type { UploadChangeParam } from "antd/es/upload";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import clsx from "clsx";
import { APP_SETTINGS } from "constant";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { CSSProperties } from "styled-components";
import uploadUtil from "utils/upload";
import "./FileUploads.style.scss";

export interface IFileUpload {
  showUploadList?: boolean;
  onChange?: (value: UploadFile<any> | UploadFile<any>[]) => void;
  value?: UploadFile[];
  isNonFileList?: boolean;

  onChangeThumbnail?: (value: any) => void;
  thumbnail?: any;
  style?: CSSProperties;
}

const FileUploadsPresenter = (props: IFileUpload) => {
  const { Dragger } = Upload;

  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!props.thumbnail) return;
    setSelectedImage(props.thumbnail.uid);
  }, [props.thumbnail]);

  useEffect(() => {
    if (!props.value || !props.value.length) return;
    setFileList(props.value);
  }, [props.value]);

  const beforeUpload = (file: RcFile, FileList: RcFile[]) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return false;
    }
    const totalSizeOfArray = FileList.map((file) => file.size).reduce((a, b) => a + b, 0);
    const isLt2M =
      file.size / 1024 / 1024 < APP_SETTINGS.UPLOAD_MAX_FILE_SIZE ||
      totalSizeOfArray / 1024 / 1024 < APP_SETTINGS.UPLOAD_MAX_FILE_SIZE;
    if (!isLt2M) {
      message.error(`Tải lên tối đa ${APP_SETTINGS.UPLOAD_MAX_FILE_SIZE}MB`);
      return false;
    }
    return isLt2M && isJpgOrPng;
  };

  const handleChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (!info.file.originFileObj) return;
    info.file.status = "done";

    // Xử lý khi tất cả ảnh đã hoàn thành việc tải lên
    if (info.fileList.every((file) => file.status === "done")) {
      console.log(60, info);

      if (props.showUploadList) {
        // send(info.fileList);
        setLoading(false);
      } else {
        if (!!props.isNonFileList) {
          uploadUtil.getBase64ForMultipleUpload([info.file], (data: { uid: string; url: string }[]) => {
            const value: any = { ...info.file, status: "done", url: data[0]?.url, uid: data[0]?.uid };
            send([value]);
          });
        } else {
          uploadUtil.getBase64ForMultipleUpload(info.fileList, (data: { uid: string; url: string }[]) => {
            send(
              info.fileList.map((file, idx) => {
                let result = { ...file };
                const find = data.find((d) => d.uid === file.uid);
                if (find) {
                  result = { ...file, status: "done", url: find.url, uid: find.uid };
                }
                return result;
              })
            );
          });
        }
        setLoading(false);
      }
    }
  };

  const send = (value: UploadFile<any> | UploadFile<any>[]) => {
    props.onChange && props.onChange(value);
  };

  const reorder = (list: any, startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const move = (source: any, destination: any, droppableSource: any, droppableDestination: any) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result: any = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destClone;

    return result;
  };

  function onDragEnd(result: any) {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sInd = source.droppableId;
    const dInd = destination.droppableId;

    if (sInd === dInd) {
      const items: any[] = reorder(fileList, source.index, destination.index);
      // const newState: any = [...fileList];
      // newState[sInd] = items;
      setFileList(items);
      send(items);
    } else {
      const result = move(fileList[sInd], fileList[dInd], source, destination);
      const newState = [...fileList];
      newState[sInd] = result[sInd];
      newState[dInd] = result[dInd];

      setFileList(newState.filter((group: any) => group.length));
    }
  }

  return (
    <div className="file-uploads" style={{ ...(props.style || {}) }}>
      <Upload
        name="avatar"
        // listType="picture-card"
        className="avatar-uploader"
        showUploadList={props.showUploadList || false}
        customRequest={uploadUtil.dummyRequest}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        multiple
        fileList={fileList}
      >
        <Button className="avatar-uploader_action" icon={<UploadOutlined />}>
          Tải lên {loading && <LoadingOutlined />}
        </Button>
      </Upload>

      <Dragger
        name="avatar"
        // listType="picture-card"
        className="avatar-uploader"
        showUploadList={props.showUploadList || false}
        customRequest={uploadUtil.dummyRequest}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        multiple
        fileList={fileList}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
        <p className="ant-upload-hint">
          Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned files.
        </p>
      </Dragger>

      <DragDropContext onBeforeCapture={(e) => console.log(e)} onDragEnd={onDragEnd}>
        <Droppable droppableId="list-images">
          {(provided, snapshot) => {
            return (
              <div className="list-images" ref={provided.innerRef} {...provided.droppableProps}>
                {(fileList || []).map((data, idx: number) => {
                  return (
                    <Draggable key={idx} draggableId={String(idx)} index={idx}>
                      {(provided, snapshot) => {
                        return (
                          <div
                            className="list-images__item"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Row
                              gutter={[12, 12]}
                              className={clsx([
                                { "list-images__item-wrapper": true },
                                { "list-images__item-wrapper-active": selectedImage === data.uid },
                              ])}
                              onClick={() => {
                                props.onChangeThumbnail && props.onChangeThumbnail(data);
                              }}
                            >
                              <Col span={10} style={{ height: "100%" }}>
                                <img
                                  src={data.url}
                                  alt={data.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  onClick={() => {
                                    data.url && setPreviewFile(data.url);
                                  }}
                                />
                              </Col>
                              <Col span={12} className="d-flex items-center">
                                <p className="text-overflow-2-lines">
                                  <span>{selectedImage === data.uid && <Tag color="#108ee9">Thumbnail</Tag>}</span>
                                  {data.name}
                                </p>
                              </Col>
                              <Col span={2} className="d-flex items-flex-start">
                                <CloseCircleOutlined
                                  style={{ fontSize: 18 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (selectedImage === data.uid) {
                                      props.onChangeThumbnail && props.onChangeThumbnail(null);
                                    }
                                    const filterFileList = fileList.filter((file) => file.uid !== data.uid);
                                    setFileList(filterFileList);
                                    send(filterFileList);
                                  }}
                                />
                              </Col>
                            </Row>
                          </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
      {previewFile && (
        <Modal width={"90%"} open={!!previewFile} footer={null} onCancel={() => setPreviewFile(null)}>
          <img alt="example" style={{ width: "100%" }} src={previewFile} />
        </Modal>
      )}
    </div>
  );
};

export default FileUploadsPresenter;
